import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, AlertCircle, ChevronDown, ChevronUp, X } from "lucide-react";
import clsx from "clsx";
import { getDiagnostics } from "../api/client";
import type { DiagnosticCheck } from "../api/types";

// Diagnostics banner: surfaces missing pipeline-critical backends.
//
// When everything is OK we render NOTHING (don't add visual noise to a
// healthy install). When something's wrong we render a single
// dismissable bar at the top of the layout. Click to expand the full
// per-backend table.
//
// Polled every 60s -- diagnostics rarely change at runtime, so we avoid
// hitting the endpoint on every render.

const DISMISS_KEY = "pbmc.diagnosticsDismissedFingerprint";

export default function DiagnosticsBanner() {
  const { data } = useQuery({
    queryKey: ["diagnostics"],
    queryFn: getDiagnostics,
    refetchInterval: 60_000,
    // Failure here shouldn't crash the app -- e.g. backend down.
    retry: false,
  });

  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem(DISMISS_KEY) || "";
  });

  if (!data) return null;
  const missing = [...data.missing_critical, ...data.missing_warning];
  const drift = data.schema_drift ?? [];
  const disk = data.disk_usage;
  // "redacted" = backend hid this for a non-admin caller; never treat
  // as a bannerable issue. Only real warn/critical pops the banner.
  const diskBad = disk && (disk.status === "warning" || disk.status === "critical");
  // Banner renders when ANY of: backends missing, schema drifted, disk full.
  if (missing.length === 0 && drift.length === 0 && !diskBad) return null;

  // Persist dismissal keyed on the union of issues so any NEW issue
  // re-pops a previously-dismissed banner.
  const driftFp = drift.map((d) => `${d.table}.${d.column}`).sort().join(",");
  const diskFp = diskBad ? `disk:${disk.status}` : "";
  const fingerprint = [missing.sort().join(","), `drift:${driftFp}`, diskFp].join("|");
  if (dismissed === fingerprint) return null;

  // Schema drift OR critical disk = critical. Warning disk + missing
  // critical backend also critical. Other combos = warning tint.
  const isCritical =
    data.missing_critical.length > 0
    || drift.length > 0
    || disk?.status === "critical";
  const Icon = isCritical ? AlertCircle : AlertTriangle;
  const colorClass = isCritical
    ? "bg-red-100 border-red-300 text-red-900"
    : "bg-amber-50 border-amber-300 text-amber-900";

  return (
    <div className={clsx("border-b px-4 py-2 text-sm flex flex-col gap-2", colorClass)}>
      <div className="flex items-center gap-2">
        <Icon size={16} className="flex-shrink-0" />
        <div className="flex-1 min-w-0">
          {drift.length > 0 && (
            <div>
              <span className="font-medium">
                Schema drift: {drift.length} column(s) in models.py missing
                from live DB.
              </span>
              {" "}
              <span className="text-xs opacity-80">
                Run <code className="font-mono">cd backend && uv run alembic upgrade head</code>{" "}
                + restart the backend service.
              </span>
            </div>
          )}
          {diskBad && disk.used_pct != null && (
            <div>
              <span className="font-medium">
                Disk {disk.status === "critical" ? "CRITICAL" : "warning"}:{" "}
                {disk.used_pct.toFixed(1)}% used on{" "}
                <code className="font-mono">{disk.data_dir}</code>
                {disk.free_bytes != null && (
                  <> ({(disk.free_bytes / 1024 ** 3).toFixed(1)} GB free).</>
                )}
              </span>
              {" "}
              <span className="text-xs opacity-80">
                {disk.status === "critical"
                  ? "Pipeline writes may fail soon. Free space immediately."
                  : "Trim old runs / move catalog references to archive disk."}
              </span>
            </div>
          )}
          {missing.length > 0 && (
            <div>
              <span className="font-medium">
                {data.missing_critical.length > 0
                  ? `Missing ${data.missing_critical.length} critical backend(s):`
                  : `Optional fallback(s) missing:`}
              </span>{" "}
              <span className="font-mono text-xs">{missing.join(", ")}</span>
              {data.missing_critical.length > 0 && (
                <span className="ml-2 text-xs opacity-80">
                  -- pipeline will silently skip those stages until installed.
                </span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-xs underline flex items-center gap-1 hover:opacity-80"
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {expanded ? "hide details" : "details"}
        </button>
        <button
          onClick={() => {
            localStorage.setItem(DISMISS_KEY, fingerprint);
            setDismissed(fingerprint);
          }}
          className="hover:opacity-80"
          title="Dismiss until backends change"
        >
          <X size={14} />
        </button>
      </div>

      {expanded && (
        <div className="text-xs space-y-2 pl-6">
          {drift.length > 0 && (
            <div className="space-y-0.5">
              <div className="font-medium">Missing columns:</div>
              {drift.map((d) => (
                <div key={`${d.table}.${d.column}`} className="font-mono">
                  · {d.table}.{d.column}
                </div>
              ))}
            </div>
          )}
          {data.checks.length > 0 && (
            <div className="space-y-1">
              {data.checks.map((c) => (
                <CheckRow key={c.key} check={c} />
              ))}
            </div>
          )}
          <div className="mt-2 text-[11px] opacity-80">
            Backends: <code className="font-mono">uv sync --frozen --extra all</code>.{" "}
            Schema: <code className="font-mono">cd backend && uv run alembic upgrade head</code>.{" "}
            Restart backend after either.
          </div>
        </div>
      )}
    </div>
  );
}

function CheckRow({ check }: { check: DiagnosticCheck }) {
  const status = check.ok ? "OK" : "MISSING";
  const statusClass = check.ok
    ? "text-emerald-700"
    : check.severity === "critical"
      ? "text-red-700 font-semibold"
      : check.severity === "warning"
        ? "text-amber-700"
        : "text-slate-500";

  return (
    <div className="flex items-baseline gap-2">
      <span className={clsx("font-mono w-20 flex-shrink-0", statusClass)}>
        [{status}]
      </span>
      <span className="font-mono w-32 flex-shrink-0">{check.key}</span>
      <span className="font-mono w-20 flex-shrink-0 opacity-60">
        {check.version || "—"}
      </span>
      <span className="opacity-80">{check.what}</span>
    </div>
  );
}
